import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { useEffect, useState } from "react";
import { QuestionContentStep } from "./QuestionContentStep";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import { SubjectYearGroupPicker } from "./SubjectYearGroupPicker";
import {
  AvailableQuestionTypes,
  QuestionTypePicker,
} from "./QuestionTypePicker";
import {
  MarkSchemeStepWrapper,
  validateMarkScheme,
} from "./MarkSchemeStepWrapper";
const steps = ["Create question", "Add mark scheme"];

export const CreateQuestionDialog = ({
  open,
  setOpen,
  createdQuestion,
  setCreatedQuestion,
  onComplete,
  forYearGroup = null,
  forSubject = null,
  forEducationLevel = null,
  quiz = false,
  assignmentCreate = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [subject, setSubject] = useState(forSubject);
  const [educationLevel, setEducationLevel] = useState(forEducationLevel);
  const [yearGroup, setYearGroup] = useState(forYearGroup);
  const [yearGroups, setYearGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [selectedQuestionType, setSelectedQuestionType] = useState(
    createdQuestion
      ? createdQuestion.questionType
      : AvailableQuestionTypes.LongText.backendName,
  );
  const [draftQuestion, setDraftQuestion] = useState(
    createdQuestion
      ? {
          questionText: createdQuestion.questionText,
          tags: createdQuestion.tags?.map((t) => t.tag) || [],
          sourceText: createdQuestion.sourceText || "",
        }
      : null,
  );

  const emptyMarkScheme = {
    modelAnswerGuidance: "",
    objectives: [],
    options: [],
  };
  const [markScheme, setMarkScheme] = useState(emptyMarkScheme);
  const ApiClient = useApiClient();

  useEffect(() => {
    if (!forSubject || !forEducationLevel) {
      fetchSubjectsAndLevels();
    }
  }, [forSubject, forEducationLevel]);
  useEffect(() => {
    if (educationLevel) {
      setYearGroups(educationLevel.yearGroups);
    } else {
      setYearGroups([]);
      setYearGroup(null);
    }
  }, [educationLevel]);

  const handleClose = () => {
    if (setCreatedQuestion) {
      setCreatedQuestion(null);
    }
    setMarkScheme(emptyMarkScheme);
    setActiveStep(0);
    setOpen(false);
  };

  useEffect(() => {
    if (createdQuestion) {
      setSelectedQuestionType(createdQuestion.questionType);
      if (createdQuestion.markScheme || createdQuestion.options?.length > 0) {
        const baseMarkScheme = createdQuestion.markScheme || emptyMarkScheme;
        setMarkScheme({
          ...baseMarkScheme,
          options: createdQuestion.options,
        });
      }
      setDraftQuestion({
        ...createdQuestion,
        markScheme: {
          ...createdQuestion.markScheme,
          options: createdQuestion.options,
        },
        tags: createdQuestion.tags?.map((t) => t.tag),
      });
      setActiveStep(1);
    }
  }, [createdQuestion]);

  const fetchSubjectsAndLevels = async () => {
    try {
      const response = await ApiClient.core.getSubjectsAndLevels();
      if (response) {
        setSubjects(response.subjects);
        setEducationLevels(response.educationLevels);
      }
    } catch (error) {
      console.error("Failed to fetch subjects and levels", error);
    }
  };

  const handleCreateQuestion = async () => {
    try {
      const response = await ApiClient.question.createQuestion({
        subject: subject.id,
        yearGroup: yearGroup.id,
        tags: draftQuestion.tags,
        questionText: draftQuestion.questionText,
        questionType: selectedQuestionType,
        sourceText: draftQuestion.sourceText,
        images: draftQuestion.images || [],
      });
      if (response && response.success) {
        setCreatedQuestion(response.question);
        enqueueSnackbar("Successfully created the question", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        console.error("Error saving question");
        enqueueSnackbar(
          "There was a problem creating the question. Please try again.",
          {
            variant: "error",
            autoHideDuration: 3000,
          },
        );
      }
    } catch (error) {
      console.error("Error creating the question:", error);
      enqueueSnackbar(
        "There was a problem creating the question. Please try again.",
        {
          variant: "error",
          autoHideDuration: 3000,
        },
      );
    }
  };

  const handleSaveQuestionAndContinue = async () => {
    try {
      const response = await ApiClient.question.updateQuestion({
        question: createdQuestion.id,
        questionText: draftQuestion.questionText,
        questionType: selectedQuestionType,
        sourceText: draftQuestion.sourceText,
        tags: draftQuestion.tags,
        markScheme: JSON.stringify(markScheme),
        images: draftQuestion.images || [],
      });
      if (response) {
        if (response.success) {
          setCreatedQuestion(response.question);
          enqueueSnackbar("Successfully saved the question", {
            variant: "success",
            autoHideDuration: 3000,
          });
          if (activeStep === 0) {
            setActiveStep(1);
          } else {
            onComplete(response.question);
            handleClose();
          }
        } else {
          console.error("Error saving question");
          enqueueSnackbar(`${response.message} Please try again.`, {
            variant: "error",
            autoHideDuration: 3000,
          });
        }
      } else {
        console.error("Error saving question");
        enqueueSnackbar(
          "There was a problem saving the question. Please try again.",
          {
            variant: "error",
            autoHideDuration: 3000,
          },
        );
      }
    } catch (error) {
      console.error("Error saving autoGenerateQuestion:", error);
      enqueueSnackbar(
        "There was a problem saving the question. Please try again.",
        {
          variant: "error",
          autoHideDuration: 3000,
        },
      );
    }
  };

  const handleMarkSchemeChange = ({
    modelAnswerGuidance,
    objectives,
    tags,
    markSchemeName,
    options,
  }) => {
    setMarkScheme({
      options: options,
      modelAnswerGuidance: modelAnswerGuidance,
      objectives: objectives,
      tags: tags,
      name: markSchemeName,
    });
  };

  const markSchemeIsComplete = validateMarkScheme(
    selectedQuestionType,
    markScheme,
  );
  const { user } = useAuthenticationStore();
  const isIndependent = user?.info?.subscriptionStatus === "independent";
  const canPickQuestionType = !assignmentCreate && !isIndependent;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableScrollLock
      fullWidth
      maxWidth={"md"}
      slotProps={{
        paper: {
          sx: {
            backgroundImage: "none",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle>Create your own question</DialogTitle>
      <DialogContent>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Box sx={{ width: "60%" }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label} onClick={() => setActiveStep(index)}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Stack>
        {activeStep === 0 && (
          <>
            {(!forSubject || !forEducationLevel) && (
              <SubjectYearGroupPicker
                subjects={subjects}
                educationLevels={educationLevels}
                yearGroups={yearGroups}
                selectedSubject={subject}
                setSelectedSubject={setSubject}
                selectedEducationLevel={educationLevel}
                setSelectedEducationLevel={setEducationLevel}
                selectedYearGroup={yearGroup}
                setSelectedYearGroup={setYearGroup}
              />
            )}
            {canPickQuestionType && (
              <QuestionTypePicker
                setSelectedQuestionType={setSelectedQuestionType}
                selectedQuestionType={selectedQuestionType}
                quiz={quiz}
                assignmentCreate={assignmentCreate}
              />
            )}
            <QuestionContentStep
              educationLevel={educationLevel}
              questionType={selectedQuestionType}
              subject={subject}
              yearGroup={yearGroup}
              existingQuestion={createdQuestion}
              onSaveDraft={setDraftQuestion}
              disabled={!subject || !educationLevel || !yearGroup}
              setMarkScheme={setMarkScheme}
            />
          </>
        )}
        {activeStep === 1 && (
          <MarkSchemeStepWrapper
            question={createdQuestion}
            markScheme={markScheme}
            onChangeMarkScheme={handleMarkSchemeChange}
            selectedQuestionType={selectedQuestionType}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          onClick={
            createdQuestion
              ? handleSaveQuestionAndContinue
              : handleCreateQuestion
          }
          variant="contained"
          disabled={
            (activeStep === 0 &&
              (!draftQuestion?.questionText ||
                !subject ||
                !educationLevel ||
                !yearGroup)) ||
            (activeStep === 1 && !markSchemeIsComplete)
          }
        >
          {activeStep === steps.length - 1 ? "Finish" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
